if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.boundPreprocessHTML = this.preprocessHTML.bind(this);
        this.cleanupTimer = null;
        this.variantUiObserver = null;
        this.flowStage = 'first';
        this.firstPick = null;
        this.promoCatalog = [];
        this.session = 0;
        this.busy = false;
        this.addEventListener('submit', this.onOfferSubmit.bind(this), true);
        this.addEventListener('click', this.onFlowClick.bind(this));
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape' && (this.chartDialog || Date.now() < (this.suppressEscapeUntil || 0))) event.stopImmediatePropagation();
        }, true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.boundPreprocessHTML);
          this.scheduleVariantUiCleanup();
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.session += 1;
        this.loadController?.abort();
        this.closeSizeChart(true);
        this.modalContent.querySelector('product-info')?.abortController?.abort();
        this.modalContent.innerHTML = '';
        this.teardownObservers();
        this.flowStage = 'first';
        this.firstPick = null;
        this.promoCatalog = [];
        this.busy = false;
        this.removeAttribute('data-sf-catalog-open');
        this.removeAttribute('data-sf-complete');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      async show(opener) {
        this.setOpenerLoading(opener, true);
        this.session += 1;
        const session = this.session;
        this.flowStage = 'first';
        this.firstPick = null;
        this.promoCatalog = [];
        this.busy = false;
        this.committed = false;
        this.cartResult = null;
        this.cartState = null;
        this.secondPick = null;
        this.firstAlreadyAdded = false;
        this.existingOfferCount = 0;
        this.catalogOpen = false;
        this.classList.add('sf-qa-enhanced');
        try {
          await this.loadProduct(opener.getAttribute('data-product-url'));
          if (session !== this.session) return;
          super.show(opener);
        } catch (error) {
          if (error.name === 'AbortError' || session !== this.session) return;
          this.modalContent.textContent = '';
          const message = document.createElement('p');
          message.className = 'sf-qa-load-error';
          message.textContent = 'We couldn’t load this shirt. Please close this window and try again.';
          this.modalContent.appendChild(message);
          super.show(opener);
        } finally {
          this.setOpenerLoading(opener, false);
        }
      }

      async loadProduct(url) {
        this.loadController?.abort();
        this.loadController = new AbortController();
        const controller = this.loadController;
        const response = await fetch(this.productURL(url), { signal: controller.signal });
        if (!response.ok) throw new Error('Product could not be loaded.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller.signal.aborted) throw new DOMException('Product loading cancelled.', 'AbortError');
        const product = html.querySelector('product-info');
        if (!product) throw new Error('Product options are unavailable.');
        const config = this.readConfig(product);
        if (this.flowStage === 'second' && (!config.eligible || !this.promoCatalog.some((item) => String(item.id) === String(config.productId)))) {
          throw new Error('This shirt is no longer available in the offer. Choose another shirt.');
        }
        this.modalContent.querySelector('product-info')?.abortController?.abort();
        this.preprocessHTML(product);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, product.outerHTML);
        window.Shopify?.PaymentButton?.init();
        window.ProductModel?.loadShopifyXR();
        this.initializePostRenderFeatures();
      }

      static readCartOffer(cart, catalog) {
        const ids = new Set((catalog || []).map((item) => String(item.id)));
        const items = (cart.items || []).filter((item) => ids.has(String(item.product_id)));
        const count = items.reduce((total, item) => total + item.quantity, 0);
        const original = items.reduce((total, item) => total + Number(item.original_line_price ?? item.price * item.quantity), 0);
        const final = items.reduce((total, item) => total + Number(item.final_line_price ?? item.line_price), 0);
        let freeUnits = Math.floor(count / 2);
        let requiredSaving = 0;
        for (const item of [...items].sort((a, b) => a.price - b.price)) {
          const units = Math.min(freeUnits, item.quantity);
          requiredSaving += units * item.price;
          freeUnits -= units;
          if (freeUnits <= 0) break;
        }
        return { count, items, confirmed: count >= 2 && count % 2 === 0 && original - final >= requiredSaving - 1 };
      }

      async showAfterProductAdd(opener, pick, cart, response, config) {
        this.session += 1;
        const session = this.session;
        this.flowStage = 'second';
        this.firstPick = pick;
        this.firstAlreadyAdded = true;
        this.offerPairId = pick.pairId;
        this.promoCatalog = config.catalog || [];
        this.existingOfferCount = cart.items.filter((item) => item.properties?._sf_bogo_pair === pick.pairId).reduce((total, item) => total + item.quantity, 0);
        this.cartResult = { ...response, id: response.items?.[0]?.id || response.id, key: response.items?.[0]?.key || response.key };
        this.cartState = cart;
        this.catalogOpen = false;
        this.committed = false;
        this.busy = false;
        this.removeAttribute('data-sf-complete');
        this.removeAttribute('data-sf-catalog-open');
        this.classList.add('sf-qa-enhanced');
        const url = new URL(this.productURL(pick.url), window.location.origin);
        url.searchParams.set('variant', pick.id);
        await this.loadProduct(url.pathname + url.search);
        if (session !== this.session) return;
        super.show(opener);
      }

      initializePostRenderFeatures() {
        this.quickConfig = this.readConfig(this.modalContent.querySelector('product-info'));
        if (!this.promoCatalog.length && this.quickConfig.eligible) this.promoCatalog = this.quickConfig.catalog || [];
        this.rebuildCompactLayout();
        this.refreshQuickAddUI();
        this.scheduleVariantUiCleanup();
        this.setupObservers();
      }

      setupObservers() {
        this.teardownObservers();
        if (!this.modalContent) return;

        this.variantUiObserver = new MutationObserver(() => this.scheduleVariantUiCleanup());
        this.variantUiObserver.observe(this.modalContent, {
          childList: true,
          subtree: true,
        });
      }

      teardownObservers() {
        if (this.variantUiObserver) {
          this.variantUiObserver.disconnect();
          this.variantUiObserver = null;
        }

        if (this.cleanupTimer) {
          window.clearTimeout(this.cleanupTimer);
          this.cleanupTimer = null;
        }
      }

      scheduleVariantUiCleanup() {
        if (this.cleanupTimer) window.clearTimeout(this.cleanupTimer);

        this.cleanupTimer = window.setTimeout(() => {
          this.normalizeVariantSelects();
          this.removeDuplicateVariantGroups();
          this.refreshQuickAddUI();
        }, 80);
      }

      setOpenerLoading(opener, isLoading) {
        if (!opener) return;

        opener.toggleAttribute('aria-disabled', isLoading);
        opener.classList.toggle('loading', isLoading);
        opener.classList.toggle('sf-is-loading', isLoading);

        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      preprocessHTML(productElement) {
        this.captureLegacyChart(productElement);
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const selectorsToRemove = [
          'pickup-availability',
          'sf-product-offer',
          'product-modal',
          'modal-dialog',
          '.od-offerbar',
          '.od-quick-offers',
          '.od-bundle-offers',
          '.od-countdown',
          '.onhow-countdown',
          '.onhow-estimated-delivery',
          '.onhow-satisfaction-container',
          '.onhow-trust-badges',
          '#ModelInfo',
          '.product__model-info',
          '.product-form__quantity',
          '.quantity__rules',
          '.price-per-item__container',
          '.product__inventory',
          '.product__tax',
          '.product__sku',
          '.product__description',
          '.product__accordion',
          '.product-popup-modal__opener',
          '.share-button',
          '.icon-with-text',
          '.product__view-details',
          '.complementary-products',
          '.shopify-payment-button',
          'sticky-buy-bar',
          '[id^="shopify-block"][id*="judge_me"]',
          '.jdgm-widget',
          '.jdgm-rev-widg',
        ];

        productElement.querySelectorAll(selectorsToRemove.join(',')).forEach((node) => node.remove());

        // Keep quick-add focused and compact: only title, price, variants, and purchase actions.
        const infoContainer = productElement.querySelector('.product__info-container');
        if (!infoContainer) return;

        const keepSelectors = [
          '.product__title',
          '[id^="price-"]',
          '[id^="Price-"]',
          'variant-selects',
          '.product-form',
        ];

        Array.from(infoContainer.children).forEach((child) => {
          const shouldKeep = keepSelectors.some((selector) => child.matches(selector) || child.querySelector(selector));
          if (!shouldKeep) child.remove();
        });
      }

      getVariantGroupKey(group) {
        const rawLabel = group.querySelector('.form__label')?.textContent || '';
        return rawLabel.replace(/\s+/g, ' ').trim().toLowerCase().replace(/:.*/, '');
      }

      normalizeVariantSelects() {
        const allVariantSelects = Array.from(this.modalContent.querySelectorAll('variant-selects'));
        if (allVariantSelects.length <= 1) return;

        // `viewTransition` leaves old nodes for 500ms with inline display:none.
        allVariantSelects
          .filter((node) => node.style.display === 'none')
          .forEach((node) => node.remove());

        const visibleVariantSelects = Array.from(this.modalContent.querySelectorAll('variant-selects')).filter(
          (node) => node.style.display !== 'none'
        );

        if (visibleVariantSelects.length <= 1) return;

        // Keep the first visible block: new content is inserted before old content.
        const keepNode = visibleVariantSelects[0];
        visibleVariantSelects.forEach((node) => {
          if (node !== keepNode) node.remove();
        });
      }

      removeDuplicateVariantGroups() {
        const variantSelects = this.modalContent.querySelector('variant-selects');
        if (!variantSelects) return;

        const groups = Array.from(variantSelects.querySelectorAll('.product-form__input'));
        if (groups.length <= 1) return;

        const seen = new Set();
        for (let index = groups.length - 1; index >= 0; index -= 1) {
          const group = groups[index];
          const key = this.getVariantGroupKey(group);
          if (!key) continue;

          if (seen.has(key)) {
            group.remove();
            continue;
          }

          seen.add(key);
        }
      }

      removeColorVariantWhenSizeExists() {
        const variantSelects = this.modalContent.querySelector('variant-selects');
        if (!variantSelects) return;

        const variantInputs = Array.from(variantSelects.querySelectorAll('.product-form__input'));
        if (!variantInputs.length) return;

        const hasSize = variantInputs.some((group) => this.getVariantGroupKey(group).startsWith('size'));
        if (!hasSize) return;

        variantInputs.forEach((group) => {
          const key = this.getVariantGroupKey(group);
          if (key.startsWith('color') || key.startsWith('colour')) {
            group.remove();
          }
        });
      }

      rebuildCompactLayout() {
        const product = this.modalContent.querySelector('.product');
        const mediaWrapper = product?.querySelector('.product__media-wrapper');
        const infoWrapper = product?.querySelector('.product__info-wrapper');
        const infoContainer = infoWrapper?.querySelector('.product__info-container');
        if (!product || !mediaWrapper || !infoWrapper || !infoContainer) return;
        if (infoContainer.querySelector('.sf-quickadd-summary')) return;

        const title = infoContainer.querySelector('.product__title');
        const price = infoContainer.querySelector('[id^="price-"], [id^="Price-"]');

        const summary = document.createElement('div');
        summary.className = 'sf-quickadd-summary';

        const mediaCol = document.createElement('div');
        mediaCol.className = 'sf-quickadd-summary__media';
        const sourceImage = mediaWrapper.querySelector('img');
        if (sourceImage) {
          const thumbnail = document.createElement('img');
          thumbnail.src = sourceImage.currentSrc || sourceImage.src || '';
          if (sourceImage.srcset) thumbnail.srcset = sourceImage.srcset;
          if (sourceImage.sizes) thumbnail.sizes = sourceImage.sizes;
          thumbnail.alt = sourceImage.alt || '';
          thumbnail.loading = 'lazy';
          thumbnail.decoding = 'async';
          mediaCol.appendChild(thumbnail);
        }

        const infoCol = document.createElement('div');
        infoCol.className = 'sf-quickadd-summary__info';
        if (title) infoCol.appendChild(title);
        if (price) infoCol.appendChild(price);

        summary.append(mediaCol, infoCol);
        infoContainer.prepend(summary);
        mediaWrapper.style.display = 'none';
        product.classList.add('sf-quickadd-reflowed');
      }


      escape(value) {
        return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
      }

      productURL(value) {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !/\/products\/[^/]+/.test(url.pathname)) throw new Error('Invalid product URL.');
        return url.pathname + url.search;
      }

      readConfig(product) {
        try {
          return JSON.parse(product?.querySelector('[data-sf-quickadd-config]')?.textContent || '{}');
        } catch (_) {
          return {};
        }
      }

      captureLegacyChart(product) {
        if (product.tagName !== 'PRODUCT-INFO' || product.querySelector('[data-sf-quickadd-chart]')) return;
        const opener = [...product.querySelectorAll('.product-popup-modal__opener')].find((item) => /size|taille|talla/i.test(item.textContent));
        const id = opener?.getAttribute('data-modal')?.replace(/^#/, '');
        const popup = id && [...product.querySelectorAll('modal-dialog')].find((item) => item.id === id);
        const source = popup?.querySelector('.product-popup-modal__content-info');
        if (!source) return;
        const template = document.createElement('template');
        template.setAttribute('data-sf-quickadd-chart', '');
        template.innerHTML = source.innerHTML;
        product.appendChild(template);
      }

      refreshQuickAddUI() {
        const info = this.modalContent.querySelector('.product__info-container');
        if (!info) return;
        const groups = [...info.querySelectorAll('variant-selects .product-form__input')];
        const color = groups.find((group) => group.classList.contains('product-form__input--swatch') || /^(color|colour|couleur|colore)/i.test(this.getVariantGroupKey(group)));
        const chartGroup = color || groups[0];
        if (color) color.classList.add('sf-qa-color-group');
        const label = chartGroup?.querySelector('.form__label');
        if (label && !label.querySelector('.sf-qa-chart-button')) {
          label.classList.add('sf-qa-option-header');
          const text = document.createElement('span');
          text.className = 'sf-qa-option-label';
          while (label.firstChild) text.appendChild(label.firstChild);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sf-qa-chart-button';
          button.dataset.sfAction = 'chart';
          button.setAttribute('aria-haspopup', 'dialog');
          button.innerHTML = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><rect x="2" y="5" width="16" height="10" rx="1.5"/><path d="M6 5v4M10 5v6M14 5v4"/></svg><span>Size Chart</span>';
          label.append(text, button);
        }
        // Products with no variant picker still have access to their own chart.
        if (!chartGroup && !info.querySelector('.sf-qa-standalone-chart')) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sf-qa-chart-button sf-qa-standalone-chart';
          button.dataset.sfAction = 'chart';
          button.textContent = 'Size Chart';
          button.setAttribute('aria-haspopup', 'dialog');
          info.querySelector('.sf-quickadd-summary')?.after(button);
        }
        if (this.quickConfig?.eligible && !info.querySelector('.sf-qa-flow')) {
          const flow = document.createElement('div');
          flow.className = 'sf-qa-flow';
          info.querySelector('.sf-quickadd-summary')?.after(flow);
          this.renderOffer();
        }
        const submit = info.querySelector('.product-form [type="submit"]');
        if (submit && this.quickConfig?.eligible && !submit.disabled && !this.busy && !this.committed) {
          const text = this.flowStage === 'first' ? 'Choose my free shirt' : (this.firstAlreadyAdded ? 'Add my second shirt' : 'Add both shirts to bag');
          const span = submit.querySelector('span');
          if (span && span.textContent.trim() !== text) span.textContent = text;
          if (!info.querySelector('.sf-qa-single-only') && this.flowStage === 'first') {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'sf-qa-single-only';
            button.dataset.sfAction = 'single';
            button.textContent = 'Add one shirt only';
            submit.closest('.product-form').after(button);
          }
        }
      }

      renderOffer() {
        const flow = this.modalContent.querySelector('.sf-qa-flow');
        if (!flow) return;
        const second = this.flowStage === 'second';
        const e = this.escape.bind(this);
        const progress = `<ol class="sf-qa-steps" aria-label="Your two-shirt offer"><li class="${second ? 'is-done' : 'is-current'}"><span>${second ? '✓' : '1'}</span> Your first shirt</li><li class="${second ? 'is-current' : ''}"><span>2</span> Your free shirt</li></ol>`;
        let content = `${progress}<div class="sf-qa-offer"><span class="sf-qa-eyebrow">BLACK FRIDAY · BUY 1, GET 1 FREE</span><h3>${second ? 'One more shirt. On us.' : 'Two shirts. One on us.'}</h3><p>${second ? (this.firstAlreadyAdded ? (this.firstPick.quantity > 1 ? 'Your shirts are in the bag. Choose one more to complete the next free-shirt pair.' : 'Your first shirt is in the bag. Choose one more to unlock your free-shirt offer.') : 'Your first pick is saved. Choose the same shirt or find a new favourite below.') : 'Choose your size, then pick one more shirt to unlock the offer.'}</p><small>The lower-priced shirt is free with your automatic offer.</small></div>`;
        if (second) {
          content += `<div class="sf-qa-saved"><img src="${e(this.firstPick.image)}" alt="" width="44" height="44"><div><span>${this.firstAlreadyAdded ? (this.firstPick.quantity > 1 ? 'FIRST PICKS IN YOUR BAG' : 'FIRST SHIRT IN YOUR BAG') : 'FIRST PICK SAVED'}</span><strong>${e(this.firstPick.title)}</strong><small>${e(this.firstPick.variantTitle)}${this.firstPick.quantity > 1 ? ` × ${this.firstPick.quantity}` : ''}</small></div><button type="button" data-sf-action="${this.firstAlreadyAdded ? 'bag' : 'edit'}">${this.firstAlreadyAdded ? 'View bag' : 'Edit'}</button></div><div class="sf-qa-pick-tabs" aria-label="Choose your second shirt"><button type="button" data-sf-action="same" aria-pressed="${!this.catalogOpen && String(this.quickConfig.productId) === String(this.firstPick.productId)}">Same shirt</button><button type="button" data-sf-action="browse" aria-pressed="${this.catalogOpen || String(this.quickConfig.productId) !== String(this.firstPick.productId)}">Choose another</button></div>`;
          if (this.catalogOpen) {
            const catalog = this.promoCatalog.filter((item) => String(item.id) !== String(this.firstPick.productId));
            content += `<div class="sf-qa-catalog"><p class="sf-qa-catalog-label">Pick your second shirt</p><div class="sf-qa-catalog-grid">${catalog.map((item) => `<button type="button" class="sf-qa-catalog-card" data-sf-action="product" data-product-id="${e(item.id)}"><img src="${e(item.image)}" alt="" width="160" height="160" loading="lazy"><span>${e(item.title)}</span><small>Choose size →</small></button>`).join('') || '<p>No other shirts are currently available. You can choose the same shirt in another size.</p>'}</div><button type="button" class="sf-qa-text-button" data-sf-action="same">Back to the same shirt</button></div>`;
          }
        }
        content += '<p class="sf-qa-message" role="status" aria-live="polite" hidden></p>';
        flow.innerHTML = content;
        this.toggleAttribute('data-sf-catalog-open', !!this.catalogOpen);
        this.modalContent.querySelector('.sf-qa-single-only')?.remove();
        this.refreshQuickAddUI();
      }

      async onFlowClick(event) {
        const button = event.target.closest('[data-sf-action]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        const action = button.dataset.sfAction;
        if (action === 'chart') return this.openSizeChart(button);
        if (this.busy) return;
        if (action === 'single') {
          const form = this.modalContent.querySelector('.product-form form');
          const submit = form?.querySelector('[type="submit"]');
          if (form && submit && !submit.disabled) {
            this.singleOnly = true;
            form.requestSubmit(submit);
          }
          return;
        }
        if (action === 'bag') return this.openBag();
        if (action === 'continue') return this.hide();
        if (action === 'browse') {
          this.catalogOpen = true;
          this.renderOffer();
          trapFocus(this, this.modalContent.querySelector('.sf-qa-catalog-card') || button);
          return;
        }
        if (!['same', 'product', 'edit'].includes(action)) return;
        const product = action === 'product' ? this.promoCatalog.find((item) => String(item.id) === button.dataset.productId) : this.firstPick;
        if (!product) return;
        this.busy = true;
        this.setMessage('Loading your shirt…');
        const session = this.session;
        const previousStage = this.flowStage;
        const previousPick = this.firstPick;
        const previousCatalogOpen = this.catalogOpen;
        if (action === 'edit') this.flowStage = 'first';
        try {
          let url = this.productURL(product.url);
          if (action !== 'product') {
            const parsed = new URL(url, window.location.origin);
            parsed.searchParams.set('variant', product.id);
            url = parsed.pathname + parsed.search;
          }
          if (action === 'edit') this.firstPick = null;
          this.catalogOpen = false;
          await this.loadProduct(url);
          if (!this.hasAttribute('open')) return;
          this.modalContent.scrollTop = 0;
          trapFocus(this, this.modalContent.querySelector('variant-selects .product-form__input--pill input:checked, variant-selects select, .product-form [type="submit"]'));
        } catch (error) {
          if (session !== this.session) return;
          this.flowStage = previousStage;
          this.firstPick = previousPick;
          this.catalogOpen = previousCatalogOpen;
          if (error.name !== 'AbortError') this.setMessage(error.message || 'Please choose another shirt.', true);
        } finally {
          if (session === this.session) {
            this.busy = false;
            this.refreshQuickAddUI();
          }
        }
      }

      getPick(form) {
        return this.constructor.pickForProduct(this.modalContent.querySelector('product-info'), this.quickConfig, form);
      }

      static pickForProduct(product, config, form) {
        const id = form.querySelector('[name="id"]')?.value;
        if (!id || form.querySelector('[type="submit"]')?.disabled) throw new Error('Please choose an available size.');
        let variant = {};
        try { variant = JSON.parse(product.querySelector('[data-selected-variant]')?.textContent || '{}'); } catch (_) {}
        const data = new FormData(form);
        const properties = {};
        for (const [key, value] of data.entries()) {
          const property = key.match(/^properties\[(.+)\]$/);
          if (property && typeof value === 'string') properties[property[1]] = value;
        }
        return {
          id: Number(id), productId: config.productId || product.dataset.productId,
          title: config.title || product.querySelector('.product__title')?.textContent.trim(),
          url: config.url || product.dataset.url,
          image: product.querySelector('.sf-quickadd-summary__media img, .product__media-wrapper img')?.src || '',
          variantTitle: variant.public_title || variant.title || '', price: Number(variant.price || 0),
          quantity: Math.max(1, parseInt(data.get('quantity') || '1', 10)),
          properties, selling_plan: data.get('selling_plan') || undefined,
        };
      }

      onOfferSubmit(event) {
        if (this.singleOnly) { this.singleOnly = false; return; }
        const form = event.target;
        if (!form.matches('form') || !form.closest('product-form') || !this.quickConfig?.eligible) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.busy || this.committed) return;
        try {
          const pick = this.getPick(form);
          if (this.flowStage === 'first') {
            this.firstPick = pick;
            this.flowStage = 'second';
            this.catalogOpen = false;
            this.renderOffer();
            this.modalContent.scrollTop = 0;
            const target = this.modalContent.querySelector('[data-sf-action="browse"]');
            trapFocus(this, target);
          } else {
            this.addPair(pick);
          }
        } catch (error) {
          this.setMessage(error.message, true);
        }
      }

      setMessage(message, error = false) {
        const target = this.modalContent.querySelector('.sf-qa-message');
        if (!target) return;
        target.hidden = !message;
        target.textContent = message;
        target.setAttribute('role', error ? 'alert' : 'status');
        target.classList.toggle('is-error', error);
      }

      get cartRoot() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }

      async fetchCart() {
        const response = await fetch(`${this.cartRoot}cart.js`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Your bag could not be checked.');
        return response.json();
      }

      async addPair(secondPick) {
        this.busy = true;
        const session = this.session;
        const pairId = this.firstAlreadyAdded ? this.offerPairId : (window.crypto?.randomUUID?.() || `sf-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        const picks = [this.firstPick, secondPick];
        const submit = this.modalContent.querySelector('.product-form [type="submit"]');
        if (submit) { submit.setAttribute('aria-disabled', 'true'); submit.classList.add('loading'); }
        this.setMessage(this.firstAlreadyAdded ? 'Adding your second shirt and checking your offer…' : 'Adding both shirts and checking your offer…');
        const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sectionIds = cartUI?.getSectionsToRender().map((section) => section.id) || ['cart-icon-bubble'];
        const payload = {
          items: (this.firstAlreadyAdded ? [secondPick] : picks).map((pick) => ({ id: pick.id, quantity: 1, properties: { ...pick.properties, _sf_bogo_pair: pairId }, ...(pick.selling_plan ? { selling_plan: pick.selling_plan } : {}) })),
          sections: sectionIds,
          sections_url: window.location.pathname,
        };
        let result;
        let statusKnown = false;
        let cartVerified = false;
        let rollbackPending = false;
        let requestAccepted = false;
        let baseline = {};
        try {
          if (this.firstAlreadyAdded) {
            const before = await this.fetchCart();
            const firstLines = before.items.filter((item) => item.properties?._sf_bogo_pair === pairId);
            this.existingOfferCount = firstLines.reduce((total, item) => total + item.quantity, 0);
            if (!this.existingOfferCount) throw new Error('Your bag has changed. Please check your bag before trying again.');
            const offer = this.constructor.readCartOffer(before, this.promoCatalog);
            if (offer.count % 2 === 0) {
              this.cartResult = null;
              this.showBagMessage('Your bag is ready to review', offer.confirmed ? 'Your eligible shirts already qualify and the free-shirt offer is applied. Review your bag before adding more.' : 'You already have an even number of eligible shirts. Please review the offer totals in your bag.');
              return;
            }
            firstLines.forEach((item) => { baseline[item.variant_id || item.id] = (baseline[item.variant_id || item.id] || 0) + item.quantity; });
          }
          const response = await fetch(`${this.cartRoot}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(payload),
          });
          statusKnown = true;
          result = await response.json();
          requestAccepted = response.ok && !result.status;
          const cart = await this.fetchCart();
          cartVerified = true;
          const added = cart.items.filter((item) => item.properties?._sf_bogo_pair === pairId);
          const count = added.reduce((sum, item) => sum + item.quantity, 0);
          const expectedCount = this.firstAlreadyAdded ? this.existingOfferCount + 1 : 2;
          if (count !== expectedCount) {
            // Shopify can partially add an out-of-stock request. Remove only
            // this attempt's uniquely marked lines; never touch existing items.
            rollbackPending = added.length > 0;
            for (const item of added) {
              const variantId = item.variant_id || item.id;
              const keepQuantity = Math.min(item.quantity, baseline[variantId] || 0);
              baseline[variantId] = Math.max(0, (baseline[variantId] || 0) - keepQuantity);
              if (keepQuantity === item.quantity) continue;
              const rollback = await fetch(`${this.cartRoot}cart/change.js`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: keepQuantity }),
              });
              if (!rollback.ok) throw new Error('Please check your bag before trying again.');
            }
            rollbackPending = false;
            requestAccepted = false;
            if (session === this.session) this.setMessage(result.description || (this.firstAlreadyAdded ? 'That size is no longer available. Your first shirt is still in the bag; choose another size.' : 'One of these sizes is no longer available. Please choose another size; the pair was not added.'), true);
            return;
          }
          requestAccepted = true;
          const original = added.reduce((sum, item) => sum + Number(item.original_line_price ?? item.price * item.quantity), 0);
          const final = added.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price), 0);
          const minimumPrice = Math.min(...picks.map((pick) => pick.price).filter((price) => price > 0));
          const confirmed = this.firstAlreadyAdded ? this.constructor.readCartOffer(cart, this.promoCatalog).confirmed : Number.isFinite(minimumPrice) && original - final >= minimumPrice - 1;
          this.cartResult = { ...result, id: result.items?.[0]?.id, key: result.items?.[0]?.key };
          this.cartState = cart;
          if (session !== this.session) { this.updateCartIcon(result.sections); return; }
          this.closeSizeChart(true);
          this.committed = true;
          this.secondPick = secondPick;
          const total = this.firstAlreadyAdded ? Number(cart.total_price ?? cart.items.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price), 0)) : final;
          this.showPairConfirmation(confirmed, total, cart.currency || this.quickConfig.currency);
          if (this.firstAlreadyAdded) this.dispatchEvent(new CustomEvent('sf:offer-completed', { detail: { cart, confirmed } }));
          this.updateCartIcon(result.sections);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: cart });
        } catch (error) {
          if (session !== this.session) return;
          // A lost response does not prove that the add failed. Do not offer
          // an immediate retry that could add the same pair twice.
          if (!statusKnown || !cartVerified || rollbackPending || requestAccepted || /check your bag/i.test(error.message)) {
            this.closeSizeChart(true);
            this.committed = true;
            const flow = this.modalContent.querySelector('.sf-qa-flow');
            flow.innerHTML = '<div class="sf-qa-confirmation"><h3>Let’s check your bag</h3><p>We couldn’t confirm the final update. Check your bag before adding these shirts again.</p><button type="button" class="sf-qa-primary" data-sf-action="bag">Check my bag</button></div>';
            this.setAttribute('data-sf-complete', '');
            trapFocus(this, flow.querySelector('button'));
          } else {
            this.setMessage(error.message || 'The offer could not be added. Please try again.', true);
          }
        } finally {
          if (session === this.session) {
            this.busy = false;
            if (submit?.isConnected) { submit.removeAttribute('aria-disabled'); submit.classList.remove('loading'); }
            this.refreshQuickAddUI();
          }
        }
      }

      updateCartIcon(sections) {
        const html = sections?.['cart-icon-bubble'];
        const icon = document.getElementById('cart-icon-bubble');
        if (!html || !icon) return;
        const documentHTML = new DOMParser().parseFromString(html, 'text/html');
        icon.innerHTML = documentHTML.querySelector('.shopify-section')?.innerHTML || html;
      }

      money(cents, currency) {
        try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100); }
        catch (_) { return (cents / 100).toFixed(2); }
      }

      showPairConfirmation(confirmed, total, currency) {
        const flow = this.modalContent.querySelector('.sf-qa-flow');
        const e = this.escape.bind(this);
        const picks = [this.firstPick, this.secondPick];
        flow.innerHTML = `<div class="sf-qa-confirmation"><span class="sf-qa-confirmation-icon" aria-hidden="true">✓</span><span class="sf-qa-eyebrow">${confirmed ? 'FREE SHIRT UNLOCKED' : 'BOTH SHIRTS ADDED'}</span><h3>${confirmed ? (this.firstAlreadyAdded ? 'Your offer is unlocked.' : 'A perfect pair.') : 'Your shirts are in the bag.'}</h3><p>${confirmed ? 'Your automatic offer has been applied.' : 'The free-shirt discount has not appeared yet. Please check your bag totals before checkout.'}</p><div class="sf-qa-confirmation-picks">${picks.map((pick) => `<div><img src="${e(pick.image)}" alt="" width="64" height="64"><strong>${e(pick.title)}</strong><span>${e(pick.variantTitle)}${this.firstAlreadyAdded && pick.quantity > 1 ? ` × ${pick.quantity}` : ''}</span></div>`).join('')}</div><div class="sf-qa-pair-total"><span>${this.firstAlreadyAdded ? 'Bag subtotal' : 'Pair total'}</span><strong>${e(this.money(total, currency))}</strong></div><button type="button" class="sf-qa-primary" data-sf-action="bag">View my bag</button><button type="button" class="sf-qa-text-button" data-sf-action="continue">Keep exploring</button></div>`;
        this.setAttribute('data-sf-complete', '');
        this.modalContent.scrollTop = 0;
        trapFocus(this, flow.querySelector('[data-sf-action="bag"]'));
      }

      showBagMessage(heading, message) {
        this.closeSizeChart(true);
        this.committed = true;
        const flow = this.modalContent.querySelector('.sf-qa-flow');
        flow.innerHTML = `<div class="sf-qa-confirmation"><h3>${this.escape(heading)}</h3><p>${this.escape(message)}</p><button type="button" class="sf-qa-primary" data-sf-action="bag">Review my bag</button></div>`;
        this.setAttribute('data-sf-complete', '');
        trapFocus(this, flow.querySelector('button'));
      }

      openBag() {
        const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = this.cartResult?.sections;
        const validSections = cartUI && sections && cartUI.getSectionsToRender().every((section) => typeof sections[section.id] === 'string');
        if (validSections) {
          const result = this.cartResult;
          cartUI.setActiveElement(this.openedBy);
          this.hide(true);
          cartUI.classList.remove('is-empty');
          try {
            cartUI.renderContents(result);
          } catch (_) {
            window.location.assign(`${this.cartRoot}cart`);
          }
        } else {
          window.location.assign(`${this.cartRoot}cart`);
        }
      }

      openSizeChart(trigger) {
        this.closeSizeChart(true);
        const product = this.modalContent.querySelector('product-info');
        const source = product?.querySelector('[data-sf-quickadd-chart]');
        const dialog = document.createElement('dialog');
        dialog.className = 'sf-qa-size-dialog';
        const id = `${this.id}-chart-${Date.now()}`;
        dialog.id = `${id}-dialog`;
        dialog.setAttribute('aria-labelledby', id);
        trigger.setAttribute('aria-controls', dialog.id);
        trigger.setAttribute('aria-expanded', 'true');
        dialog.innerHTML = `<div class="sf-qa-size-header"><div><h2 id="${this.escape(id)}">Size Chart</h2><p>${this.escape(this.quickConfig?.title || product?.querySelector('.product__title')?.textContent.trim())}</p></div><button type="button" class="sf-qa-size-close" aria-label="Close size chart"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button></div><div class="sf-qa-size-content"></div>`;
        const content = dialog.querySelector('.sf-qa-size-content');
        if (source?.innerHTML.trim()) {
          content.innerHTML = source.innerHTML;
          content.querySelectorAll('script, iframe, object, embed, form').forEach((item) => item.remove());
          content.querySelectorAll('*').forEach((item) => [...item.attributes].forEach((attribute) => {
            if (/^on/i.test(attribute.name) || /^(href|src)$/i.test(attribute.name) && /^javascript:/i.test(attribute.value.trim())) item.removeAttribute(attribute.name);
          }));
        } else {
          const message = document.createElement('p');
          message.textContent = 'This product does not have a size chart assigned yet. Please contact us for measurements before choosing your size.';
          content.appendChild(message);
        }
        this.chartDialog = dialog;
        this.chartTrigger = trigger;
        dialog.querySelector('.sf-qa-size-close').addEventListener('click', () => this.closeSizeChart());
        dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeSizeChart(); });
        dialog.addEventListener('keyup', (event) => { if (event.code === 'Escape') event.stopPropagation(); });
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeSizeChart();
        });
        this.appendChild(dialog);
        this.querySelector('.quick-add-modal__content').inert = true;
        removeTrapFocus();
        dialog.showModal();
        dialog.querySelector('button').focus();
      }

      closeSizeChart(immediate = false) {
        const dialog = this.chartDialog;
        if (!dialog) return;
        this.chartDialog = null;
        this.suppressEscapeUntil = Date.now() + 300;
        this.querySelector('.quick-add-modal__content').inert = false;
        const trigger = this.chartTrigger;
        trigger?.setAttribute('aria-expanded', 'false');
        trigger?.removeAttribute('aria-controls');
        const finish = () => {
          dialog.close();
          dialog.remove();
          if (!immediate && this.hasAttribute('open') && trigger?.isConnected) trapFocus(this, trigger);
        };
        if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
        dialog.classList.add('is-closing');
        window.setTimeout(finish, 160);
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}


if (!customElements.get('sf-product-offer')) {
  customElements.define('sf-product-offer', class extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (this.initialized || !this.isConnected || this.closest('quick-add-modal')) return;
      this.initialized = true;
      this.product = this.closest('product-info');
      this.form = this.product?.querySelector('product-form form');
      this.primary = this.form?.querySelector('.product-form__submit');
      try { this.config = JSON.parse(this.product?.querySelector('[data-sf-quickadd-config]')?.textContent || '{}'); }
      catch (_) { this.config = {}; }
      if (!this.config.eligible || !this.form || !this.primary) return;

      this.events = new AbortController();
      this.snapshots = new Map();
      this.hidden = false;
      this.createOfferModal();
      this.product.addEventListener('submit', this.prepareFirstAdd.bind(this), { capture: true, signal: this.events.signal });
      this.product.addEventListener('sf:product-added', this.onProductAdded.bind(this), { signal: this.events.signal });
      this.product.addEventListener('change', () => this.syncStickyLabel(), { signal: this.events.signal });
      this.addEventListener('click', this.onAction.bind(this), { signal: this.events.signal });
      this.modal.addEventListener('sf:offer-completed', (event) => {
        this.cartState = event.detail.cart;
        this.lastResponse = this.modal.cartResult;
        this.setStatus(event.detail.confirmed ? 'ready' : 'check', event.detail.confirmed ? 'Your free-shirt offer is unlocked.' : 'Your shirts are in the bag.', event.detail.confirmed ? 'Your automatic discount has been applied. You can review both shirts in your bag.' : 'The free-shirt discount has not appeared yet. Review the totals in your bag before checkout.');
      }, { signal: this.events.signal });
      this.syncStickyLabel();
      customElements.whenDefined('sticky-buy-bar').then(() => { if (this.isConnected) this.syncStickyLabel(); });
    }

    createOfferModal() {
      const id = `QuickAddPDP-${this.config.productId}-${this.product.dataset.section}`;
      const template = document.createElement('template');
      template.innerHTML = `<quick-add-modal id="${id}" class="quick-add-modal"><div role="dialog" aria-label="Choose your free shirt" aria-modal="true" class="quick-add-modal__content" tabindex="-1"><button id="ModalClose-${id}" type="button" class="quick-add-modal__toggle" aria-label="Close offer"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button><div id="QuickAddInfo-${id}" class="quick-add-modal__content-info"></div></div></quick-add-modal>`;
      this.appendChild(template.content);
      this.modal = document.getElementById(id);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.events?.abort();
      if (this.modal?.hasAttribute('open')) this.modal.hide();
      this.modal?.remove();
    }

    syncStickyLabel() {
      const sticky = this.product?.querySelector('sticky-buy-bar');
      if (!sticky || !this.form?.isConnected) return;
      const quantity = Math.max(1, parseInt(new FormData(this.form).get('quantity') || '1', 10));
      sticky.defaultLabel = quantity % 2 === 0 ? 'Add shirts to bag' : 'Add & choose free shirt';
      sticky.dataset.defaultLabel = sticky.defaultLabel;
      sticky.syncState?.();
    }

    prepareFirstAdd(event) {
      if (event.target !== this.form) return;
      if (this.pending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const submit = event.submitter || this.primary;
      if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
      try {
        const pick = customElements.get('quick-add-modal').pickForProduct(this.product, this.config, this.form);
        const pairId = window.crypto?.randomUUID?.() || `pdp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        let marker = this.form.querySelector('[name="properties[_sf_bogo_pair]"]');
        if (!marker) {
          marker = document.createElement('input');
          marker.type = 'hidden';
          marker.name = 'properties[_sf_bogo_pair]';
          this.form.appendChild(marker);
        }
        marker.value = pairId;
        pick.pairId = pairId;
        this.snapshots.clear();
        this.snapshots.set(pairId, pick);
      } catch (_) {
        // Native validation and sold-out messages remain responsible for errors.
      }
    }

    async onProductAdded(event) {
      if (event.target !== this.form.closest('product-form')) return;
      const pairId = event.detail.formData.get('properties[_sf_bogo_pair]');
      const pick = this.snapshots.get(pairId);
      if (!pick) return;
      event.preventDefault();
      this.pending = true;
      this.lastPick = pick;
      const response = event.detail.response;
      this.lastResponse = { ...response, id: response.id || response.items?.[0]?.id, key: response.key || response.items?.[0]?.key };
      this.modal.updateCartIcon(response.sections);
      this.setStatus('check', 'Your shirt is in the bag.', 'Checking your free-shirt offer…');
      try {
        this.cartState = await this.modal.fetchCart();
        const offer = customElements.get('quick-add-modal').readCartOffer(this.cartState, this.config.catalog);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: this.cartState });
        if (!this.isConnected) return;
        if (offer.count % 2 === 1) {
          this.setStatus('waiting', 'One more shirt unlocks your free one.', pick.quantity > 1 ? 'Your selected shirts are already in the bag. Choose one more to complete the next pair.' : 'Your first shirt is already in the bag. Choose the same shirt or another, then select its size.');
          await this.modal.showAfterProductAdd(event.detail.submitter || this.primary, pick, this.cartState, this.lastResponse, this.config);
        } else {
          this.setStatus(offer.confirmed ? 'ready' : 'check', offer.confirmed ? 'Your free-shirt offer is unlocked.' : 'Your shirts are in the bag.', offer.confirmed ? 'You have enough eligible shirts and your automatic discount is applied.' : 'Review your bag to check the automatic offer before checkout.');
          this.renderCurrentBag();
        }
      } catch (_) {
        if (this.isConnected) this.setStatus('waiting', 'Your shirt is in the bag.', 'Choose your next shirt to complete the offer, or review your bag first.');
      } finally {
        this.pending = false;
      }
    }

    setStatus(state, heading, message) {
      this.dataset.offerState = state;
      this.querySelector('.sf-pdp-offer-before').hidden = true;
      const after = this.querySelector('.sf-pdp-offer-after');
      after.hidden = false;
      after.querySelector('[data-offer-state]').textContent = state === 'ready' ? 'FREE SHIRT UNLOCKED' : state === 'waiting' ? 'FIRST PICK ADDED' : 'YOUR BAG UPDATE';
      after.querySelector('[data-offer-heading]').textContent = heading;
      after.querySelector('[data-offer-message]').textContent = message;
      after.querySelector('[data-pdp-offer-action="choose"]').hidden = state !== 'waiting';
    }

    async onAction(event) {
      const button = event.target.closest('[data-pdp-offer-action]');
      if (!button || this.pending) return;
      event.preventDefault();
      if (button.dataset.pdpOfferAction === 'bag') return this.openCurrentBag();
      if (!this.lastPick) return;
      this.pending = true;
      button.setAttribute('aria-disabled', 'true');
      try {
        const cart = await this.modal.fetchCart();
        const firstStillInBag = cart.items.some((item) => item.properties?._sf_bogo_pair === this.lastPick.pairId);
        if (!firstStillInBag) {
          this.setStatus('check', 'Your bag has changed.', 'Review your bag, then add your chosen first shirt to start a new offer.');
          return;
        }
        const offer = customElements.get('quick-add-modal').readCartOffer(cart, this.config.catalog);
        if (offer.count % 2 === 0) {
          this.setStatus(offer.confirmed ? 'ready' : 'check', offer.confirmed ? 'Your free-shirt offer is unlocked.' : 'Your bag is ready to review.', 'You already have enough eligible shirts. Review your bag before adding another.');
          return this.openCurrentBag();
        }
        await this.modal.showAfterProductAdd(button, this.lastPick, cart, this.lastResponse, this.config);
      } catch (_) {
        this.setStatus('waiting', 'Your first shirt is still saved here.', 'We couldn’t load the next step. Review your bag or try choosing your free shirt again.');
      } finally {
        this.pending = false;
        button.removeAttribute('aria-disabled');
      }
    }

    renderCurrentBag() {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const valid = cart && this.lastResponse?.sections && cart.getSectionsToRender().every((section) => typeof this.lastResponse.sections[section.id] === 'string');
      if (!valid) return false;
      try {
        cart.setActiveElement(this.primary);
        cart.classList.remove('is-empty');
        cart.renderContents(this.lastResponse);
        return true;
      } catch (_) { return false; }
    }

    async openCurrentBag() {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        try {
          const ids = cart.getSectionsToRender().map((section) => section.id);
          const response = await fetch(`${this.modal.cartRoot}cart?sections=${encodeURIComponent(ids.join(','))}`, { cache: 'no-store' });
          if (!response.ok) throw new Error('Bag unavailable');
          this.lastResponse = { ...this.lastResponse, sections: await response.json() };
          if (this.renderCurrentBag()) return;
        } catch (_) {}
      }
      window.location.assign(`${this.modal.cartRoot}cart`);
    }
  });
}
